import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	ButtonTypes,
} from "@repo/ui";
import { useState } from "react";

import type { ApplicationSetting, SettingEnumOption } from "../../../models";

import { useDeleteSettingMutation } from "../hooks";
import { segmentParts } from "../settingsTree";
import style from "./style.module.scss";
import { ValueText } from "./valueText";

interface Props {
	setting: ApplicationSetting;
	/** For an Enum setting, its definition's options, so the value shows its label */
	enumOptions?: SettingEnumOption[] | null;
	onClose: () => void;
}

/** Asks before deleting a setting row, naming its setting, segment and value */
export function DeleteSettingDialog({ setting, enumOptions, onClose }: Props) {
	const deleteSetting = useDeleteSettingMutation();
	const [error, setError] = useState<string>();
	const segment = segmentParts(setting).join(", ") || "Company-wide";

	async function confirm() {
		setError(undefined);
		const result = await deleteSetting.mutateAsync(setting.applicationSettingId).catch(() => undefined);
		if (result?.deleted) return onClose();
		setError("The setting couldn't be deleted. It may already be gone; reload the page to check.");
	}

	return (
		<AlertDialog open onOpenChange={(open) => !open && onClose()}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Delete this setting?</AlertDialogTitle>
					<AlertDialogDescription>
						{setting.name} for {segment}, with value{" "}
						<q>
							<ValueText value={setting.value} enumOptions={enumOptions} masked={setting.isMasked} />
						</q>
						.
					</AlertDialogDescription>
				</AlertDialogHeader>
				{error && <p className={style.dialogError}>{error}</p>}
				<AlertDialogFooter>
					<AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
					<AlertDialogAction
						types={[ButtonTypes.Danger]}
						disabled={deleteSetting.isPending}
						onClick={() => void confirm()}
					>
						Delete
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
